import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Landmark, Loader2, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { runAiJob } from '../lib/aiJob';
import { fmtDate } from '../lib/dates';
import { OrganoCard, AreaRow } from '../components/catalog/OrganoCard';
import { TrendForm } from '../components/trends/TrendForm';
import { TrendCard } from '../components/trends/TrendCard';

const TABS = [['areas', 'Áreas del derecho'], ['organos', 'Tribunales y recursos'], ['tendencias', 'Tendencias']];

export default function AreasCourtsPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('areas');
  const [cat, setCat] = useState(null);
  const [tr, setTr] = useState(null);
  const [sel, setSel] = useState(null);
  const [matters, setMatters] = useState([]);
  const [verifying, setVerifying] = useState(false);
  const admin = !!(user?.is_admin || user?.role === 'admin');
  const loadCat = useCallback(() => api.get('/legal-catalog').then((r) => setCat(r.data)).catch(() => toast.error('No se pudo cargar el catálogo')), [api]);
  const loadTr = useCallback(() => api.get('/court-trends').then((r) => { setTr(r.data); setSel((s) => s || r.data.analyses[0]?.id || null); }).catch(() => {}), [api]);
  useEffect(() => { loadCat(); loadTr(); api.get('/crm/matters', { params: { limit: 100 } }).then((r) => setMatters(r.data.matters || [])).catch(() => {}); }, [loadCat, loadTr]); // eslint-disable-line react-hooks/exhaustive-deps
  const verify = async () => {
    setVerifying(true);
    try { const r = await runAiJob(api, '/legal-catalog/verify', {}, { timeout: 600000, interval: 4000 }); toast.success(`${r.verificadas} bases verificadas, ${r.fallidas} fallidas`); loadCat(); } catch (e) { toast.error(e.message); } finally { setVerifying(false); }
  };
  return (
    <div data-testid="areas-courts-page" className="min-h-screen bg-slate-950 text-white">
      <div className="mx-auto max-w-6xl px-4 pb-40 pt-6 sm:px-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white"><ArrowLeft className="h-4 w-4" />Volver</button>
        <h1 className="mt-3 flex items-center gap-3 text-4xl font-bold tracking-tight sm:text-5xl"><Landmark className="h-9 w-9 text-amber-400" />Áreas y tribunales</h1>
        <div className="mt-6 flex flex-wrap gap-2">{TABS.map(([k, l]) => <button key={k} data-testid={`areas-tab-${k}`} onClick={() => setTab(k)} className={`rounded-full px-4 py-1.5 text-sm ${tab === k ? 'bg-amber-500 font-semibold text-slate-950' : 'border border-slate-700 text-slate-300'}`}>{l}</button>)}</div>
        {tab !== 'tendencias' && cat && (
          <div className="mt-6 flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400">
              <p className="max-w-3xl">{cat.advertencia}</p>
              <span data-testid="catalog-last-verified">Última verificación en LeyChile: {cat.ultima_verificacion ? fmtDate(cat.ultima_verificacion, true) : 'nunca'}</span>
              {admin && <button data-testid="catalog-verify" disabled={verifying} onClick={verify} className="ml-auto flex items-center gap-1 rounded-full border border-slate-700 px-3 py-1 text-slate-300 disabled:opacity-50">{verifying ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}Verificar ahora</button>}
            </div>
            {tab === 'areas' ? cat.areas.map((a) => <AreaRow key={a.id} a={a} admin={admin} onChange={loadCat} />) : cat.organos.map((o) => <OrganoCard key={o.id} o={o} admin={admin} onChange={loadCat} />)}
          </div>
        )}
        {tab === 'tendencias' && tr && (
          <div className="mt-6 flex flex-col gap-5">
            <p className="max-w-3xl text-sm text-slate-400">Sentencias del buscador oficial del Poder Judicial. Se muestra una tendencia solo con {tr.min_sample} sentencias o más. Lo que propone Cerebro es una hipótesis hasta que la valides.</p>
            <TrendForm meta={tr} matters={matters} onDone={(id) => { setSel(id); loadTr(); }} />
            {!!tr.analyses.length && <select data-testid="trend-history" value={sel || ''} onChange={(e) => setSel(e.target.value)} className="w-fit max-w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm">
              {tr.analyses.map((a) => <option key={a.id} value={a.id}>{fmtDate(a.analizado_at, true)} · {tr.tribunales[a.query.tribunal]} · {a.query.tipo_norma} {a.query.num_norma} {a.query.articulo ? `art. ${a.query.articulo}` : ''} ({a.stats.n})</option>)}
            </select>}
            {sel && <TrendCard key={sel} id={sel} />}
          </div>
        )}
      </div>
    </div>
  );
}
